import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Ticket, CircleDot, Clock, CheckCircle2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const priorityConfig = {
  low: { label: 'Laag', textColor: 'text-slate-500', borderColor: 'border-slate-500' },
  normal: { label: 'Normaal', textColor: 'text-blue-500', borderColor: 'border-blue-500' },
  high: { label: 'Hoog', textColor: 'text-orange-500', borderColor: 'border-orange-500' },
  urgent: { label: 'Urgent', textColor: 'text-red-500', borderColor: 'border-red-500' }
};

const statusConfig = {
  open: { label: 'Open', color: 'bg-yellow-500', icon: CircleDot },
  in_progress: { label: 'In behandeling', color: 'bg-blue-500', icon: Clock },
  closed: { label: 'Afgehandeld', color: 'bg-green-500', icon: CheckCircle2 }
};

export const MyTicketsPanel = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!user?.id) return;
    api.get(`/tickets?assigned_to=${user.id}`)
      .then(res => {
        const sorted = [...res.data].sort((a, b) => {
          const aClosed = a.status === 'closed' ? 1 : 0;
          const bClosed = b.status === 'closed' ? 1 : 0;
          if (aClosed !== bClosed) return aClosed - bClosed;
          return (b.created_at || '').localeCompare(a.created_at || '');
        });
        setTickets(sorted);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [user?.id]);

  const openCount = tickets.filter(t => t.status !== 'closed').length;

  return (
    <Card className="border-border/50" data-testid="my-tickets-panel">
      <CardHeader className="flex flex-row items-center justify-between pb-3">
        <div>
          <CardTitle className="text-lg flex items-center gap-2">
            <Ticket className="w-5 h-5 text-amber-400" />
            Mijn Tickets
            {openCount > 0 && (
              <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30" data-testid="my-tickets-open-count">
                {openCount} open
              </Badge>
            )}
          </CardTitle>
          <CardDescription>Tickets die aan jou zijn toegewezen</CardDescription>
        </div>
        <Button variant="ghost" size="sm" onClick={() => navigate('/tickets')} data-testid="my-tickets-view-all">
          Bekijk alle
        </Button>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="h-20 animate-pulse bg-muted rounded-lg" />
        ) : tickets.length === 0 ? (
          <p className="text-center text-muted-foreground py-8" data-testid="my-tickets-empty">
            Geen tickets aan jou toegewezen
          </p>
        ) : (
          <div className="space-y-2 max-h-80 overflow-y-auto pr-1">
            {tickets.map((ticket) => {
              const status = statusConfig[ticket.status] || statusConfig.open;
              const prio = priorityConfig[ticket.priority] || priorityConfig.normal;
              return (
                <div
                  key={ticket.id}
                  onClick={() => navigate(`/tickets?ticket=${ticket.id}`)}
                  className={`flex items-center justify-between gap-3 p-3 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors cursor-pointer ${ticket.status === 'closed' ? 'opacity-50' : ''}`}
                  data-testid={`my-ticket-item-${ticket.id}`}
                >
                  <div className="min-w-0">
                    <p className="font-medium truncate">{ticket.title}</p>
                    <p className="text-xs text-muted-foreground truncate">
                      {ticket.customer_name ? `${ticket.customer_name} · ` : ''}
                      {ticket.created_at ? new Date(ticket.created_at).toLocaleDateString('nl-BE') : ''}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Badge variant="outline" className={`${prio.borderColor} ${prio.textColor}`}>
                      {prio.label}
                    </Badge>
                    <Badge className={`${status.color} text-white`}>{status.label}</Badge>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default MyTicketsPanel;
